import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Link2, Loader2, RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import logger from '@/lib/logger';

const EXPIRY_OPTIONS = [
  { value: 30, labelKey: 'scenarios.expiry30' },
  { value: 90, labelKey: 'scenarios.expiry90' },
  { value: '', labelKey: 'scenarios.expiryNever' },
];

const STATUS_STYLES = {
  active: 'text-[#527F19] border-[#527F19]/35 bg-[#F1F8E8]',
  revoked: 'text-[#B42318] border-[#B42318]/35 bg-[#B42318]/[0.06]',
  expired: 'text-[#66707A] border-[#DDE1E5] bg-[#F5F6F7]',
};

function ShareRow({ share, onRevoke, onRegenerate, busy }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const url = `${window.location.origin}${share.path}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('scenarios.copied'));
    } catch (err) {
      logger.debug('[share] clipboard unavailable:', err?.message || err);
      toast.error(t('scenarios.copied'));
    }
  };

  return (
    <li className="py-3 space-y-2" data-testid={`share-row-${share.share_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center border px-1.5 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-[0.08em] ${STATUS_STYLES[share.status] || STATUS_STYLES.expired}`}
          style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          data-testid={`share-status-${share.share_id}`}
        >
          {t(`scenarios.link${share.status.charAt(0).toUpperCase()}${share.status.slice(1)}`)}
        </span>
        <span className="text-[11px] text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {t('scenarios.version', { version: share.version })}
          {' · '}
          {share.expires_at
            ? t('scenarios.expiresOn', { date: new Date(share.expires_at).toLocaleDateString() })
            : t('scenarios.neverExpires')}
          {' · '}
          {t('scenarios.opens', { count: share.opens })}
        </span>
      </div>

      <p className="text-[11px] text-[#66707A] break-all" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
        {share.scenario_names.join(' · ')}
      </p>

      {confirming ? (
        <div className="flex flex-wrap items-center gap-2 rounded-[6px] border border-[#B42318]/35 bg-[#B42318]/[0.04] px-3 py-2">
          <p className="text-xs text-[#8F1C13] flex-1 min-w-[180px]">{t('scenarios.revokeBody')}</p>
          <Button
            onClick={() => { setConfirming(false); onRevoke(share); }}
            className="h-8 bg-[#B42318] hover:bg-[#8F1C13] text-white text-xs rounded-md"
            data-testid={`share-revoke-confirm-${share.share_id}`}
          >
            {t('scenarios.revokeConfirm')}
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirming(false)}
            className="h-8 border-[#DDE1E5] text-[#3A4249] text-xs rounded-md"
            data-testid={`share-revoke-cancel-${share.share_id}`}
          >
            {t('assumptions.cancel')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={copy}
            disabled={share.status !== 'active'}
            className="h-8 border-[#DDE1E5] text-[#3A4249] text-xs rounded-md disabled:opacity-40"
            data-testid={`share-copy-${share.share_id}`}
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" /> {t('scenarios.copyLink')}
          </Button>
          <a
            href={share.path}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center h-8 px-3 rounded-md border border-[#DDE1E5] text-xs text-[#3A4249] hover:bg-[#F5F6F7] transition-colors duration-150 ${share.status !== 'active' ? 'pointer-events-none opacity-40' : ''}`}
            data-testid={`share-open-${share.share_id}`}
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" /> {t('scenarios.openLink')}
          </a>
          <Button
            variant="outline"
            onClick={() => onRegenerate(share)}
            disabled={busy}
            className="h-8 border-[#DDE1E5] text-[#3A4249] text-xs rounded-md"
            data-testid={`share-regenerate-${share.share_id}`}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" /> {t('scenarios.regenerate')}
          </Button>
          {share.status === 'active' && (
            <Button
              variant="outline"
              onClick={() => setConfirming(true)}
              className="h-8 border-[#DDE1E5] text-[#66707A] hover:text-[#B42318] text-xs rounded-md"
              data-testid={`share-revoke-${share.share_id}`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" /> {t('scenarios.revoke')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Publish and manage read-only comparison links.
 * Publishing freezes a snapshot; republishing issues a NEW token and version
 * and kills the previous link immediately.
 */
export function ShareComparisonDialog({ open, onOpenChange, triggerRef, groupId, estimateIds, shares, onChanged, onSnapshot }) {
  const { t } = useTranslation();
  const [expiry, setExpiry] = useState(90);
  const [busy, setBusy] = useState(false);

  const groupShares = (shares || []).filter(s => s.scenario_group_id === groupId);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post('/api/scenarios/shares', {
        scenario_group_id: groupId,
        estimate_ids: estimateIds,
        expiry_days: expiry === '' ? null : Number(expiry),
      }, { withCredentials: true });
      toast.success(t('scenarios.shareCreated'));
      if (onSnapshot) onSnapshot(data.snapshot);
      if (onChanged) onChanged();
      try {
        await navigator.clipboard.writeText(`${window.location.origin}${data.path}`);
        toast.success(t('scenarios.copied'));
      } catch (err) {
        logger.debug('[share] clipboard unavailable:', err?.message || err);
      }
    } catch (err) {
      logger.warn('[share] create failed:', err?.message || err);
      toast.error(t('scenarios.shareFailed'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (share) => {
    try {
      await axios.post(`/api/scenarios/shares/${share.share_id}/revoke`, {}, { withCredentials: true });
      toast.success(t('scenarios.shareRevoked'));
      if (onChanged) onChanged();
    } catch (err) {
      logger.warn('[share] revoke failed:', err?.message || err);
      toast.error(t('scenarios.shareFailed'));
    }
  };

  const regenerate = async (share) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`/api/scenarios/shares/${share.share_id}/regenerate`, {},
        { withCredentials: true });
      toast.success(t('scenarios.shareRegenerated'));
      if (onSnapshot) onSnapshot(data.snapshot);
      if (onChanged) onChanged();
    } catch (err) {
      logger.warn('[share] regenerate failed:', err?.message || err);
      toast.error(t('scenarios.shareFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#DDE1E5] max-w-xl max-h-[85vh] overflow-y-auto"
        aria-describedby={undefined}
        data-testid="share-comparison-dialog"
        onCloseAutoFocus={(event) => {
          if (triggerRef?.current) {
            event.preventDefault();
            triggerRef.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-[#171A1D]">{t('scenarios.shareTitle')}</DialogTitle>
        </DialogHeader>

        <p className="text-xs text-[#66707A]">{t('scenarios.shareIntro')}</p>

        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="block text-[11px] text-[#66707A] mb-1">{t('scenarios.expiry')}</span>
            <select
              value={expiry}
              onChange={(e) => setExpiry(e.target.value === '' ? '' : Number(e.target.value))}
              className="h-9 rounded-[6px] border border-[#DDE1E5] bg-white px-2 text-sm text-[#171A1D] focus:border-[#78B928] outline-none"
              data-testid="share-expiry-select"
            >
              {EXPIRY_OPTIONS.map(option => (
                <option key={String(option.value)} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </select>
          </label>
          <Button
            onClick={create}
            disabled={busy}
            className="h-9 bg-[#527F19] hover:bg-[#3F6114] text-white rounded-md"
            data-testid="share-create-btn"
          >
            {busy
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
              : <Link2 className="w-4 h-4 mr-2" aria-hidden="true" />}
            {t('scenarios.createLink')}
          </Button>
        </div>

        <div className="border-t border-[#DDE1E5] pt-3">
          <h3 className="text-xs font-medium text-[#3A4249]">{t('scenarios.existingLinks')}</h3>
          {groupShares.length === 0 ? (
            <p className="mt-2 text-xs text-[#8A929B]" data-testid="share-empty">{t('scenarios.noLinks')}</p>
          ) : (
            <ul x-excluded="true" className="divide-y divide-[#DDE1E5]" data-testid="share-list">
              {groupShares.map(share => (
                <ShareRow
                  key={share.share_id}
                  share={share}
                  busy={busy}
                  onRevoke={revoke}
                  onRegenerate={regenerate}
                />
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
